import { useEffect, useState } from "react"
import dayjs from "dayjs"
import { Button, Card, Checkbox, DatePicker, Divider, Flex, Form, Input, InputNumber, Modal, Radio, Segmented } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import CustomDrawer from "ui/Drawer"
import { useDevice } from "hooks/useDevice"
import { spacings } from "themes/variables"
import { AFFECTS, PLACEHOLDER_TICKET_TYPES, PRICE_DIRECTION, PRICE_TYPE, PRICE_TYPE_OPTIONS } from "../../../constants/pricingConstants"
import { findConflictingSeasonalAdjustment } from "../../../helpers/adjustmentConflicts"
import styles from "./AddSeasonalAdjustmentModal.module.css"

const AddSeasonalAdjustmentModal = ({ open, editingItem, ticketTypes, existingAdjustments = [], onCancel, onSave }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const [form] = Form.useForm()
  const [affects, setAffects] = useState(AFFECTS.ALL)
  const [selectedTickets, setSelectedTickets] = useState([])
  const [isRepeatEachYear, setIsRepeatEachYear] = useState(true)
  const [direction, setDirection] = useState(PRICE_DIRECTION.INCREASE)
  const [priceType, setPriceType] = useState(PRICE_TYPE.PERCENT)
  const [isTicketSelectionMissing, setIsTicketSelectionMissing] = useState(false)
  // Name of the adjustment whose dates clash with this one, shown inline under the pickers.
  const [conflictingName, setConflictingName] = useState(null)

  useEffect(() => {
    if (open && editingItem) {
      form.setFieldsValue({
        name: editingItem.name,
        priceValue: editingItem.priceValue,
        startDate: editingItem.startDate,
        endDate: editingItem.endDate,
      })
      setAffects(editingItem.affects || AFFECTS.ALL)
      setSelectedTickets(editingItem.selectedTickets || [])
      setIsRepeatEachYear(editingItem.isRepeatEachYear ?? true)
      setDirection(editingItem.direction || PRICE_DIRECTION.INCREASE)
      setPriceType(editingItem.priceType || PRICE_TYPE.PERCENT)
    } else if (!open) {
      form.resetFields()
      setAffects(AFFECTS.ALL)
      setSelectedTickets([])
      setIsRepeatEachYear(true)
      setDirection(PRICE_DIRECTION.INCREASE)
      setPriceType(PRICE_TYPE.PERCENT)
    }
    setIsTicketSelectionMissing(false)
    setConflictingName(null)
  }, [open, editingItem, form])

  const toggleTicket = (id) => {
    setIsTicketSelectionMissing(false)
    setSelectedTickets((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]))
  }

  const handleAffectsChange = (value) => {
    setAffects(value)
    setIsTicketSelectionMissing(false)
    setConflictingName(null)
  }

  const handleSave = async () => {
    // Checked before validateFields so this error shows alongside field errors, not after them.
    const isMissingTickets = ticketTypes.length > 0 && affects === AFFECTS.SELECTED && selectedTickets.length === 0
    setIsTicketSelectionMissing(isMissingTickets)
    try {
      const values = await form.validateFields()
      if (isMissingTickets) return
      const adjustment = {
        ...(editingItem ? { id: editingItem.id } : {}),
        ...values,
        affects,
        selectedTickets: affects === AFFECTS.SELECTED ? selectedTickets : [],
        isRepeatEachYear,
        direction,
        priceType,
      }

      // Overlapping adjustments both apply to the same booking and their deltas stack,
      // so a clash is blocked here rather than silently double-charging later.
      const conflict = findConflictingSeasonalAdjustment(adjustment, existingAdjustments)
      if (conflict) {
        setConflictingName(conflict.name || "")
        return
      }

      onSave(adjustment)
    } catch {
      // AntD shows inline field errors
    }
  }

  const footer = (
    <Flex gap={spacings.S}>
      <Button shape="round" color="primary" variant="outlined" size="large" onClick={onCancel}>
        {t("experiences.pricing.adjustment_modal.cancel")}
      </Button>
      <Button shape="round" color="primary" variant="solid" size="large" onClick={handleSave}>
        {t("experiences.pricing.adjustment_modal.save")}
      </Button>
    </Flex>
  )

  const modalTitle = t(
    editingItem
      ? "experiences.pricing.seasonal_adjustment_modal.edit_title"
      : "experiences.pricing.seasonal_adjustment_modal.title"
  )

  const content = (
    <Form
      form={form}
      layout="vertical"
      requiredMark={(label, { required }) => (
        <>
          {label}
          {required && " *"}
        </>
      )}
    >
        <Card className={styles.contentCard}>
          <Flex vertical gap={spacings.XL}>
            <Form.Item
              name="name"
              label={t("experiences.pricing.adjustment_modal.name_label")}
              required
              rules={[{ required: true, message: t("experiences.pricing.adjustment_modal.name_required") }]}
              tooltip={t("experiences.pricing.adjustment_modal.name_tooltip")}
            >
              <Input placeholder={t("experiences.pricing.seasonal_adjustment_modal.name_placeholder")} size="large" />
            </Form.Item>

            <Flex vertical gap={spacings.M}>
              <Text fw={500} size="l">{t("experiences.pricing.adjustment_modal.applies_on")}</Text>
              <Flex vertical gap={spacings.XS}>
                <Flex gap={spacings.M}>
                  <Form.Item
                    name="startDate"
                    label={t("experiences.pricing.seasonal_adjustment_modal.start_date")}
                    required
                    rules={[{ required: true, message: t("experiences.pricing.seasonal_adjustment_modal.start_date_required") }]}
                    className={styles.dateField}
                  >
                    <DatePicker
                      size="large"
                      format="MM/DD/YYYY"
                      className={styles.datePicker}
                      disabledDate={(current) => current && current < dayjs().startOf("day")}
                      onChange={() => {
                        setConflictingName(null)
                        form.setFieldValue("endDate", null)
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, curr) => prev.startDate !== curr.startDate}
                  >
                    {({ getFieldValue }) => {
                      const startDate = getFieldValue("startDate")
                      return (
                        <Form.Item
                          name="endDate"
                          label={t("experiences.pricing.seasonal_adjustment_modal.end_date")}
                          required
                          rules={[{ required: true, message: t("experiences.pricing.seasonal_adjustment_modal.end_date_required") }]}
                          className={styles.dateField}
                        >
                          <DatePicker
                            size="large"
                            format="MM/DD/YYYY"
                            className={styles.datePicker}
                            disabledDate={(current) => current && (current < dayjs().startOf("day") || (startDate && current <= startDate))}
                            onChange={() => setConflictingName(null)}
                          />
                        </Form.Item>
                      )
                    }}
                  </Form.Item>
                </Flex>

                {conflictingName !== null && (
                  <Text size="s" className={styles.errorText}>
                    {t("experiences.pricing.adjustment_modal.duplicate_dates", { name: conflictingName })}
                  </Text>
                )}

                <Flex align="flex-start" gap={spacings.XS} className={styles.infoBox}>
                  <InfoCircleOutlined className={styles.infoBoxIcon} />
                  <Text className={styles.infoBoxText}>
                    {t("experiences.pricing.seasonal_adjustment_modal.applies_on_info")}
                  </Text>
                </Flex>

                <Checkbox
                  checked={isRepeatEachYear}
                  onChange={(e) => {
                    setConflictingName(null)
                    setIsRepeatEachYear(e.target.checked)
                  }}
                >
                  {t("experiences.pricing.seasonal_adjustment_modal.repeat_each_year")}
                </Checkbox>
              </Flex>
            </Flex>

            <Divider className={styles.divider} />

            <Flex vertical gap={spacings.M}>
              <Text fw={500} size="l">{t("experiences.pricing.adjustment_modal.affects")}</Text>
              <Radio.Group value={affects} onChange={(e) => handleAffectsChange(e.target.value)}>
                <Flex gap={spacings.XL}>
                  <Radio value={AFFECTS.ALL}>{t("experiences.pricing.adjustment_modal.affects_all")}</Radio>
                  <Radio value={AFFECTS.SELECTED}>{t("experiences.pricing.adjustment_modal.affects_selected")}</Radio>
                </Flex>
              </Radio.Group>
              {affects === AFFECTS.SELECTED && (
                <Flex vertical gap={spacings.XS}>
                  <Flex gap={spacings.XS} wrap>
                    {ticketTypes.map((ticket) => (
                      <button
                        key={ticket.id}
                        type="button"
                        onClick={() => toggleTicket(ticket.id)}
                        className={`${styles.chip} ${selectedTickets.includes(ticket.id) ? styles.chipActive : ""}`}
                      >
                        {ticket.label}
                      </button>
                    ))}
                  </Flex>
                  {isTicketSelectionMissing && (
                    <Text size="s" className={styles.errorText}>{t("experiences.pricing.adjustment_modal.ticket_type_required")}</Text>
                  )}
                </Flex>
              )}
            </Flex>

            <Divider className={styles.divider} />

            <Flex vertical gap={spacings.M}>
              <Text fw={500} size="l">{t("experiences.pricing.adjustment_modal.price_adjustment")}</Text>
              <Radio.Group value={direction} onChange={(e) => setDirection(e.target.value)}>
                <Flex gap={spacings.XL}>
                  <Radio value={PRICE_DIRECTION.INCREASE}>{t("experiences.pricing.adjustment_modal.increase")}</Radio>
                  <Radio value={PRICE_DIRECTION.DECREASE}>{t("experiences.pricing.adjustment_modal.decrease")}</Radio>
                </Flex>
              </Radio.Group>
              <Flex vertical gap={spacings.XS}>
                <Flex align="center" gap={spacings.XS}>
                  <Form.Item
                    name="priceValue"
                    noStyle
                    rules={[{ required: true, message: t("experiences.pricing.adjustment_modal.value_required") }]}
                  >
                    <InputNumber className={styles.priceInput} size="large" min={0} max={priceType === PRICE_TYPE.PERCENT ? 100 : undefined} placeholder={"0"} />
                  </Form.Item>
                  <Segmented
                    value={priceType}
                    onChange={setPriceType}
                    options={PRICE_TYPE_OPTIONS}
                    size="large"
                    className={styles.segmented}
                  />
                </Flex>
                <Flex align="flex-start" gap={spacings.XS} className={styles.infoBox}>
                  <InfoCircleOutlined className={styles.infoBoxIcon} />
                  <Text className={styles.infoBoxText}>
                    {t("experiences.pricing.adjustment_modal.base_rate_info")}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Card>
    </Form>
  )

  if (isMobile) {
    return (
      <CustomDrawer open={open} setOpen={() => onCancel()} title={modalTitle}>
        {content}
        {footer}
      </CustomDrawer>
    )
  }

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={modalTitle}
      width={1040}
      footer={footer}
    >
      {content}
    </Modal>
  )
}

AddSeasonalAdjustmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  editingItem: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    priceValue: PropTypes.number,
    startDate: PropTypes.object,
    endDate: PropTypes.object,
    affects: PropTypes.string,
    selectedTickets: PropTypes.arrayOf(PropTypes.string),
    isRepeatEachYear: PropTypes.bool,
    direction: PropTypes.string,
    priceType: PropTypes.string,
  }),
  ticketTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  onCancel: PropTypes.func.isRequired,
  existingAdjustments: PropTypes.arrayOf(PropTypes.object),
  onSave: PropTypes.func.isRequired,
}

AddSeasonalAdjustmentModal.defaultProps = {
  editingItem: null,
  ticketTypes: PLACEHOLDER_TICKET_TYPES,
}

export default AddSeasonalAdjustmentModal
